import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Store from '@apply/store';
import { Authentication } from '@apply/store/src/user/types';
import { authenticatedFetch } from '@apply/store/src/cross';
import { Footer } from '../common_v2/components/Footer';
import { logApplicationInsightsEvent } from '../common/functions/applicationInsightFunctions';
import { LoginForm } from './LoginForm';
import { useAuthentication } from './hooks/useAuthentication';
import { useIpAddress } from './hooks/useIpAddress';
import { getPostLoginDestination } from './postLoginRedirect';

// Pulled from the Cross Store file so that we don't modify the base for backwards compatibility.
// This should be refactored into the Login API as ApplyLogin or similar in the future.
const crossStoreLoginV2 = async (
    email: string,
    password: string,
    ipaddress: string
): Promise<{ authentication?: Authentication; err?: string }> => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify({ email, password, ipaddress }),
    };
    const baseUrl = import.meta.env.VITE_API_URL;
    const response = await authenticatedFetch(`${baseUrl}/account/principal/login`, requestOptions);
    return await Store.CrossStore.processLoginResponse(response);
};

export const LoginPage: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { login, logout } = useAuthentication();
    const ipAddress = useIpAddress();

    const requestedEmail = new URLSearchParams(location.search).get('email') ?? '';

    const [email, setEmail] = useState<string>(requestedEmail);
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [attempts, setAttempts] = useState<number>(0);

    useEffect(() => {
        logout();
    }, [logout]);

    const handleLogin = async () => {
        /* istanbul ignore if */
        if (!isEmailValid || !isPasswordValid) {
            return;
        }
        setAttempts((prev) => prev + 1);
        setSubmitting(true);
        setError(null);

        const result = await login(email, password, ipAddress, async (e, p, ip) => {
            dispatch(Store.BorrowerStore.Actions.reset());
            dispatch(Store.PropertiesStore.Actions.reset());
            dispatch(Store.FormsStore.Actions.reset());
            return crossStoreLoginV2(e, p, ip);
        });
        setSubmitting(false);

        if (result.authentication) {
            logApplicationInsightsEvent(`${email} has logged in successfully.`);
            navigate(getPostLoginDestination(location.search));
        } else {
            setError(result.err || 'Email or Password is incorrect');
        }
    };

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    <LoginForm
                        email={email}
                        onEmailChange={setEmail}
                        onEmailValidation={setIsEmailValid}
                        password={password}
                        onPasswordChange={setPassword}
                        onPasswordValidation={setIsPasswordValid}
                        onSubmit={handleLogin}
                        canSubmit={isEmailValid && isPasswordValid && !submitting}
                        submitting={submitting}
                        error={error}
                        attempts={attempts}
                    />
                </div>
            </div>
            <Footer />
        </>
    );
};
